import { useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { t } from '../i18n.ts'
import './InstallHint.css'

const DISMISS_KEY = 'oya-annotated:install-hint-dismissed:v1'

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>
}

export function isStandalone(): boolean {
  if (typeof window === 'undefined') return false
  const iosStandalone = (window.navigator as { standalone?: boolean }).standalone === true
  return iosStandalone || window.matchMedia('(display-mode: standalone)').matches
}

export function isIos(): boolean {
  if (typeof navigator === 'undefined') return false
  return /iphone|ipad|ipod/i.test(navigator.userAgent)
}

function wasDismissed(): boolean {
  try {
    return window.localStorage.getItem(DISMISS_KEY) === '1'
  } catch {
    return false
  }
}

/**
 * Prompts the user to install the app.
 *
 * On iOS this is not decoration: web push is only available to a home-screen web
 * app, so this sheet is the gate on the whole notification feature. There is no
 * install prompt API on iOS, so the instructions have to be written out.
 *
 * Two suppressions matter. A dismissal sticks, so someone who does not want the app
 * on their home screen is not nagged on every visit. And it never renders over a
 * `?delt=` share link — a friend who followed an emailed link should see the concert
 * they were sent, not an install ad.
 */
export function InstallHint() {
  const location = useLocation()
  const [dismissed, setDismissed] = useState(() => wasDismissed())
  const [installEvent, setInstallEvent] = useState<BeforeInstallPromptEvent | null>(null)

  useEffect(() => {
    function onBeforeInstallPrompt(event: Event) {
      event.preventDefault()
      setInstallEvent(event as BeforeInstallPromptEvent)
    }
    window.addEventListener('beforeinstallprompt', onBeforeInstallPrompt)
    return () => window.removeEventListener('beforeinstallprompt', onBeforeInstallPrompt)
  }, [])

  const isSharedLink = new URLSearchParams(location.search).has('delt')
  // Home surface only. On a concert page it would push the artwork and the
  // annotation controls below the fold to advertise something the user came here
  // to do something else instead of.
  const onDayView = location.pathname.startsWith('/dag/')

  if (dismissed || !onDayView || isStandalone() || isSharedLink) return null

  const ios = isIos()
  if (!ios && !installEvent) return null

  function dismiss() {
    try {
      window.localStorage.setItem(DISMISS_KEY, '1')
    } catch {
      // A blocked storage write only costs us the persistence of the dismissal.
    }
    setDismissed(true)
  }

  async function install() {
    if (!installEvent) return
    await installEvent.prompt()
    await installEvent.userChoice
    setInstallEvent(null)
    dismiss()
  }

  return (
    <aside className="install-hint" aria-label={ios ? t.install.iosTitle : t.install.androidTitle}>
      <div className="install-hint__body">
        <p className="install-hint__title">{ios ? t.install.iosTitle : t.install.androidTitle}</p>
        <p className="install-hint__text">{ios ? t.install.iosBody : t.install.androidBody}</p>
      </div>
      <div className="install-hint__actions">
        {!ios && installEvent && (
          <button type="button" className="install-hint__primary" onClick={install}>
            {t.install.androidAction}
          </button>
        )}
        <button type="button" className="install-hint__dismiss" onClick={dismiss}>
          {t.install.dismiss}
        </button>
      </div>
    </aside>
  )
}
